import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import {
	Company,
	CompanyResolutionType,
	Filter,
	ICompanyUpdate,
	SharedStats,
	UpdateNotification,
	ReviewNotification,
	TagWithCount,
	Redirect,
	TagWithCountAndCanonicalString,
	urlIdentifierForCompany,
	CompanySearchResult,
} from 'company-finder-common';
import { AuthnService, DeploymentContext, Summary } from '@Common';
import { ServiceBase } from '@Common';
import { SearchBody } from '../../models/search-body.js';
import _ from 'lodash-es';
import { LogService } from '../log/log.service.js';

@Injectable({ providedIn: 'root' })
export class CompanyService extends ServiceBase {
	public currentSearchResults: Company[] = null;
	private searchUrl: string;
	private aggregateSearchUrl: string;

	constructor(
		_httpClient: HttpClient,
		_context: DeploymentContext,
		private _router: Router,
		private logService: LogService,
		private authnService: AuthnService
	) {
		super(_httpClient, _context, '/company');
		this.searchUrl = _context.buildApiUrl('/search/company');
		this.aggregateSearchUrl = _context.buildApiUrl(
			'/search/company/aggregate'
		);
	}

	public async getById(id: string): Promise<Company> {
		return this.get<Company>(`${this._apiUrl}/${id}`);
	}

	public async getByEncodedNameOrOppId(
		encodedName: string,
		companyResolutionType: CompanyResolutionType = CompanyResolutionType.Details
	): Promise<Company> {
		const result = await this.get<Company | Redirect>(
			`${this._apiUrl}/by-name-or-opp-id/${encodedName}/${companyResolutionType}`
		);

		if ((result as Redirect).redirectTo !== undefined) {
			this._router.navigate([
				`/company/${(result as Redirect).redirectTo}`,
			]);
		}

		return result as Company;
	}

	public async getByNameOrOppId(
		name: string,
		companyResolutionType: CompanyResolutionType = CompanyResolutionType.Details
	): Promise<Company> {
		return this.getByEncodedNameOrOppId(
			urlIdentifierForCompany(name),
			companyResolutionType
		);
	}

	public async getTagCounts(): Promise<TagWithCount[]> {
		return this.get<TagWithCount[]>(`${this._apiUrl}/tags/count`);
	}

	public async getTagsWithCountsAndCanonical(): Promise<
		TagWithCountAndCanonicalString[]
	> {
		const allTags = await this.getTagCounts();
		return _.orderBy(
			allTags.map((tagWithCount) => ({
				tag: tagWithCount.tag,
				count: tagWithCount.count,
				canonicalString: tagWithCount.tag.toLowerCase(),
			})),
			'canonicalString'
		);
	}

	public async getTags(): Promise<string[]> {
		return this.get<string[]>(`${this._apiUrl}/tags/list`);
	}

	// FUTURE - the division between company service and search service feels too fuzzy
	public async searchAndFilter(
		predicate: string,
		from: number,
		size: number,
		filter: Filter,
		by: string = null
	): Promise<CompanySearchResult> {
		const body = new SearchBody(
			this._context.comprehensiveSummary.allSectorsWithCounts,
			predicate,
			filter,
			from,
			size,
			by
		);

		body.filter = this.reduceFilter(body.filter);

		const result = await this.post<CompanySearchResult>(
			this.searchUrl,
			body
		);

		this.currentSearchResults = result?.items ?? [];

		return (
			result ?? {
				items: [],
				totalItems: 0,
			}
		);
	}

	public async filterAggregate(filter: Filter): Promise<SharedStats> {
		return this.searchAndFilterAggregate(filter);
	}

	public async filterSummary(filter: Filter): Promise<Summary> {
		const result = await this.filterAggregate(filter);
		return new Summary(result);
	}

	public async searchAndFilterAggregate(
		filter: Filter,
		predicate: string = ''
	): Promise<SharedStats> {
		if (predicate.trim() === '' && filter.isShowAll()) {
			return this._context.comprehensiveResults;
		}

		const filterParam = this.reduceFilter(filter, true).toString();

		const searchUrl = `${
			this.aggregateSearchUrl
		}?filter=${encodeURIComponent(filterParam)}&${this.getCacheableParams(
			filter.isUserSpecific
		)}`;
		return this.get<SharedStats>(`${searchUrl}`, {
			extraOptions: { withCredentials: true },
			skipURIEncoding: true,
		});
	}

	private getCacheableParams(includeUser: boolean): string {
		return includeUser
			? `${this._context.cacheableParams}&user=${this.authnService.userId}`
			: this._context.cacheableParams;
	}

	/** In order to simplify our query strings, we do not want to include any filter values
	 * that do not influence the results. This strips out any values that are equivalent
	 * to the defualts, including removing locations/sectors/subsectors that contain
	 * the full list of choices.
	 */
	public reduceFilter(
		filter: Filter,
		isForAggregate: boolean = false
	): Filter {
		const reducedFilter = filter.clone();

		Object.entries(reducedFilter).forEach(([k, v]) => {
			if (
				v === null ||
				v === false ||
				v === '' ||
				(Array.isArray(v) && v.length === 0)
			) {
				delete reducedFilter[k];
			}
		});

		if (
			this._context.comprehensiveSummary.locations.length ===
			reducedFilter.locations?.length
		) {
			delete reducedFilter.locations;
		}

		if (
			this._context.comprehensiveSummary.sectors.length ===
			reducedFilter.primarySectors?.length
		) {
			delete reducedFilter.primarySectors;
		}

		if (
			this._context.comprehensiveSummary.subsectors.length ===
			reducedFilter.primarySubSectors?.length
		) {
			delete reducedFilter.primarySubSectors;
		}

		if (
			this._context.comprehensiveSummary.sectors.length ===
			reducedFilter.secondarySectors?.length
		) {
			delete reducedFilter.secondarySectors;
		}

		if (
			this._context.comprehensiveSummary.subsectors.length ===
			reducedFilter.secondarySubSectors?.length
		) {
			delete reducedFilter.secondarySubSectors;
		}

		if (isForAggregate) {
			// Sort has no meaning on aggregate
			delete reducedFilter.sort;
		}

		return reducedFilter;
	}

	public async companiesForExport(
		predicate: string,
		filter: Filter
	): Promise<ArrayBuffer> {
		const requestBody = new SearchBody(
			this._context.comprehensiveSummary.allSectorsWithCounts,
			predicate,
			filter
		);

		return this.post(`${this._apiUrl}/export`, requestBody, {
			responseType: 'arraybuffer',
		});
	}

	public async getWordData(company: Company): Promise<ArrayBuffer> {
		return this.post(
			`${this._apiUrl}/generate/word`,
			{ company },
			{ responseType: 'arraybuffer' }
		);
	}

	public async getPdfData(company: Company): Promise<ArrayBuffer> {
		return this.post(
			`${this._apiUrl}/generate/pdf`,
			{ company },
			{ responseType: 'arraybuffer' }
		);
	}

	public async getWordDataForSearch(
		predicate: string,
		filter: Filter
	): Promise<ArrayBuffer> {
		return this.post(
			`${this._apiUrl}/generate/word/search`,
			{ predicate, filter },
			{ responseType: 'arraybuffer' }
		);
	}

	public async getPdfDataForSearch(
		predicate: string,
		filter: Filter,
		from: number,
		size: number
	): Promise<ArrayBuffer> {
		return this.post(
			`${this._apiUrl}/generate/pdf/search`,
			{ predicate, filter, from, size },
			{ responseType: 'arraybuffer' }
		);
	}

	public async reviewCompanyUpdate(
		companyUpdate: ICompanyUpdate
	): Promise<ICompanyUpdate> {
		return this.post<ICompanyUpdate>(`${this._apiUrl}/review`, {
			companyUpdate,
		});
	}

	public async updateCompany(
		companyUpdate: ICompanyUpdate
	): Promise<ICompanyUpdate> {
		return this.post<ICompanyUpdate>(`${this._apiUrl}/update`, {
			companyUpdate,
		});
	}

	public async deletePendingUpdateByOpportunityId(
		opportunityId: string
	): Promise<unknown> {
		return this.delete<unknown>(
			`${this._apiUrl}/update/by-opportunity-id/${opportunityId}`
		);
	}

	public async deletePendingAddedComplexUpdateByModelId(
		opportunityId: string,
		type: string,
		modelId: string,
		deleteCompanyUpdate: boolean
	): Promise<unknown> {
		return this.delete<unknown>(
			`${this._apiUrl}/update/${opportunityId}/${type}/${modelId}/${deleteCompanyUpdate}`
		);
	}

	public async getSavedOrPendingUpdateByOpportunityId(
		opportunityId: string
	): Promise<ICompanyUpdate> {
		try {
			return this.get<ICompanyUpdate>(
				`${this._apiUrl}/update/pending/by-opportunity-id/${opportunityId}`
			);
		} catch (error) {
			this.logService.logErrorOnServer(error);
			return null;
		}
	}

	public async notification(companyId: string): Promise<UpdateNotification> {
		return this.get<UpdateNotification>(
			`${this._apiUrl}/notification/${companyId}`
		);
	}

	public async reviews(
		companyNames: string[]
	): Promise<ReviewNotification[]> {
		return this.post<ReviewNotification[]>(
			`${this._apiUrl}/reviews/count`,
			{ companyNames }
		);
	}
}
